import React, { useState, useCallback } from 'react';
import { ArrowUpRight, ArrowDownRight, TrendingDown, Wallet, PieChart as PieIcon, TrendingUp, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell,
  Treemap,
} from 'recharts';
import { useAppState } from '../store/AppContext';
import { fetchAllPrices, UpdateProgress } from '../utils/priceUpdateService';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import {
  getTotalNAV, getTotalCost,
  getAssetUnrealizedGain, getAssetUnrealizedGainPercent, getTotalRealizedGain,
  getBucketProgress, getTotalLiabilities, getNetWorth, getTotalAnnualIncome,
  getAssetClassBreakdown, resolveClassColor, CASH_WALLET_ASSET_ID,
  getAssetCurrentValue, getAssetAnnualIncome,
} from '../utils/calculations';
import { formatVND } from '../utils/format';
import { Money, SensitiveValue } from './ui/SensitiveValue';

// ── Shared tooltip style ───────────────────────────────────────────────────────
const tooltipStyle = {
  contentStyle: { backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)', borderRadius: 10, fontSize: 12, color: 'var(--tx-1)' },
  labelStyle:   { color: 'var(--tx-3)' },
  itemStyle:    { color: 'var(--tx-3)' },
  cursor:       { fill: 'var(--chart-grid)' },
};

// ── Label ──────────────────────────────────────────────────────────────────────
function SectionLabel({ icon, label, color }: { icon: React.ReactNode; label: string; color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
      <div style={{ width: 26, height: 26, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${color}18`, border: `1px solid ${color}28` }}>
        {icon}
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, valueColor = 'var(--tx-1)', insight, bg, border, accent, extra }: {
  label: string; value: React.ReactNode; valueColor?: string;
  insight?: React.ReactNode; bg?: string; border?: string; accent?: string;
  extra?: React.ReactNode;
}) {
  return (
    <div style={{ borderRadius: 14, padding: '16px 18px', background: bg || 'var(--bg-card)', border: border || '1px solid var(--bd)', position: 'relative', overflow: 'hidden' }}>
      {accent && <div style={{ position: 'absolute', top: -20, right: -20, width: 72, height: 72, borderRadius: '50%', background: accent }} />}
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--tx-4)', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: valueColor, letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
        {extra}
      </div>
      {insight && <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 7, lineHeight: 1.4 }}>{insight}</div>}
    </div>
  );
}

// ── Pie Tooltip ───────────────────────────────────────────────────────────────
function PieTooltip({ active, payload, total }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0';
  return (
    <div style={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)', borderRadius: 10, padding: '10px 14px', fontSize: 12, minWidth: 170, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: d.payload.color }} />
        <span style={{ color: 'var(--tx-2)', fontWeight: 700 }}>{d.name}</span>
      </div>
      <div style={{ color: 'var(--tx-1)', fontWeight: 700, fontSize: 14, marginBottom: 2 }}><Money value={d.value} /></div>
      <div style={{ color: 'var(--tx-4)' }}>Tỷ trọng: <strong style={{ color: 'var(--tx-3)' }}>{pct}%</strong></div>
    </div>
  );
}

// ── Fund Treemap ───────────────────────────────────────────────────────────────
function FundTreemap({ assets, buckets, onNavigateToFund }: { assets: any[]; buckets: any[]; onNavigateToFund?: (id: string) => void }) {
  const [showZero,    setShowZero]    = useState(false);
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  const [mousePos,    setMousePos]    = useState<{ x: number; y: number } | null>(null);

  const grandTotal = assets.reduce((s: number, a: any) => s + a.currentPrice * a.quantity, 0);

  const allFunds = buckets.map((b: any) => {
    const nav = assets
      .filter((a: any) => a.bucketId === b.id)
      .reduce((s: number, a: any) => s + a.currentPrice * a.quantity, 0);
    return {
      name:   b.name,
      id:     b.id,
      nav,
      target: b.targetValue || 0,
      color:  b.color || '#64748b',
      pct:    grandTotal > 0 ? (nav / grandTotal) * 100 : 0,
    };
  });

  const withNav   = allFunds.filter((f: any) => f.nav > 0);
  const zeroFunds = showZero ? allFunds.filter((f: any) => f.nav <= 0) : [];

  const treeData     = withNav.map((f: any) => ({ ...f, size: f.nav }));
  const hoveredFund  = hoveredName ? withNav.find((f: any) => f.name === hoveredName) : null;

  // ── Custom treemap cell ──────────────────────────────────────────────────
  const renderCell = (props: any) => {
    const { x, y, width, height, name } = props;
    const fund = withNav.find((f: any) => f.name === name);
    if (!fund || width < 4 || height < 4) return <g />;

    const isHov    = hoveredName === name;
    const isDimmed = !!hoveredName && !isHov;
    const fSize    = Math.min(12, Math.max(8, Math.floor(width / 7)));
    const showName = width > 38 && height > 20;
    const showPct  = width > 38 && height > 40;
    const label    = fund.name.length > 14 && width < 90
      ? fund.name.slice(0, 11) + '…'
      : fund.name;

    return (
      <g
        onMouseEnter={() => setHoveredName(name)}
        onMouseLeave={() => setHoveredName(null)}
        onClick={() => onNavigateToFund && onNavigateToFund(fund.id)}
        style={{ cursor: onNavigateToFund ? 'pointer' : 'default' }}
      >
        <rect
          x={x + 2} y={y + 2} width={width - 4} height={height - 4}
          rx={6} ry={6}
          fill={fund.color}
          fillOpacity={isDimmed ? 0.28 : isHov ? 0.96 : 0.72}
          stroke={isHov ? 'rgba(255,255,255,0.5)' : 'var(--bg-app)'}
          strokeWidth={isHov ? 2 : 1.5}
          style={{ transition: 'fill-opacity 0.12s, stroke-width 0.12s' }}
        />
        {showName && (
          <text
            x={x + width / 2}
            y={y + height / 2 - (showPct ? fSize * 0.7 : 0)}
            textAnchor="middle" dominantBaseline="middle"
            fontSize={fSize} fontWeight={700}
            fill="rgba(255,255,255,0.93)"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {label}
          </text>
        )}
        {showPct && (
          <text
            x={x + width / 2}
            y={y + height / 2 + fSize * 1.1}
            textAnchor="middle" dominantBaseline="middle"
            fontSize={Math.max(8, fSize - 2)}
            fill="rgba(255,255,255,0.58)"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {fund.pct.toFixed(1)}%
          </text>
        )}
      </g>
    );
  };

  return (
    <div style={{ borderRadius: 14, padding: '14px 16px', background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
      {/* Header + toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--tx-4)' }}>
          Phân bổ theo Quỹ
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
          onClick={() => setShowZero(v => !v)}
        >
          <div style={{ width: 28, height: 16, borderRadius: 8, position: 'relative', background: showZero ? '#10b981' : 'var(--bd)', transition: 'background 0.2s', flexShrink: 0 }}>
            <div style={{ position: 'absolute', top: 3, left: showZero ? 14 : 3, width: 10, height: 10, borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
          </div>
          <span style={{ fontSize: 9.5, color: 'var(--tx-4)', whiteSpace: 'nowrap' }}>Hiển thị quỹ = 0</span>
        </div>
      </div>

      {withNav.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--tx-5)', fontSize: 12, padding: '30px 0' }}>
          Tất cả quỹ có NAV = 0
        </div>
      ) : (
        /* Treemap container — tracks mouse for the floating tooltip */
        <div
          style={{ position: 'relative' }}
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setMousePos({ x: e.clientX - r.left, y: e.clientY - r.top });
          }}
          onMouseLeave={() => { setMousePos(null); setHoveredName(null); }}
        >
          <ResponsiveContainer width="100%" height={182}>
            <Treemap
              data={treeData}
              dataKey="size"
              content={renderCell}
              isAnimationActive={false}
            />
          </ResponsiveContainer>

          {/* Floating tooltip */}
          {hoveredFund && mousePos && (
            <div style={{
              position: 'absolute',
              left: Math.min(mousePos.x + 14, 999),
              top: Math.max(4, mousePos.y - 68),
              background: 'var(--tooltip-bg)',
              border: '1px solid var(--tooltip-bd)',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 12,
              minWidth: 172,
              pointerEvents: 'none',
              zIndex: 50,
              boxShadow: '0 8px 24px rgba(0,0,0,0.38)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: 3, backgroundColor: hoveredFund.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, color: 'var(--tx-1)', fontSize: 13 }}>{hoveredFund.name}</span>
              </div>
              <div style={{ color: 'var(--tx-1)', fontWeight: 700, fontSize: 14 }}><Money value={hoveredFund.nav} /></div>
              <div style={{ color: 'var(--tx-4)', marginTop: 3 }}>
                Tỷ trọng: <strong style={{ color: 'var(--tx-2)' }}>{hoveredFund.pct.toFixed(1)}%</strong>
              </div>
              {hoveredFund.target > 0 && (
                <div style={{ color: 'var(--tx-4)', marginTop: 5, paddingTop: 5, borderTop: '1px solid var(--bd)' }}>
                  Mục tiêu: <strong style={{ color: '#fbbf24' }}><Money value={hoveredFund.target} /></strong>
                </div>
              )}
              {onNavigateToFund && (
                <div style={{ color: '#38bdf8', marginTop: 5, fontSize: 10 }}>↗ Click để xem chi tiết</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Zero-NAV funds — shown as pill badges below treemap */}
      {zeroFunds.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--bd-sub)' }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--tx-5)', marginBottom: 6 }}>
            Quỹ chưa có tài sản
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {zeroFunds.map((f: any) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6, border: `1px solid ${f.color}28`, background: `${f.color}0c` }}>
                <div style={{ width: 6, height: 6, borderRadius: 2, background: f.color, opacity: 0.45 }} />
                <span style={{ fontSize: 10, color: 'var(--tx-5)' }}>{f.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Col 2: Phân bổ ────────────────────────────────────────────────────────────
function ColPhanBo({ assets, assetClasses, buckets, onNavigateToFund }: { assets: any[]; assetClasses: any[]; buckets: any[]; onNavigateToFund?: (id: string) => void }) {
  const classData = getAssetClassBreakdown(assets, assetClasses);
  const total     = classData.reduce((s, d) => s + d.value, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Pie Chart */}
      <div style={{ borderRadius: 14, padding: '14px 16px', background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--tx-4)', marginBottom: 10 }}>Cơ cấu lớp tài sản</div>
        {classData.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--tx-5)', fontSize: 12, padding: '20px 0' }}>Chưa có dữ liệu</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Pie + legend ngang */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 96, height: 96, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={classData} cx="50%" cy="50%" innerRadius={28} outerRadius={44}
                      dataKey="value" stroke="var(--bg-card)" strokeWidth={2} paddingAngle={1} isAnimationActive={false}>
                      {classData.map(d => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Tooltip content={<PieTooltip total={total} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legend — 1 col ≤5 items, 2 cols ≥6 items */}
              <div style={{
                flex: 1, minWidth: 0,
                display: 'grid',
                gridTemplateColumns: classData.length >= 6 ? 'repeat(2, 1fr)' : '1fr',
                gap: '3px 8px',
                alignContent: 'start',
              }}>
                {classData.map(d => {
                  const pct = total > 0 ? (d.value / total * 100) : 0;
                  return (
                    <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                      <div style={{ width: 6, height: 6, borderRadius: 2, backgroundColor: d.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 10, color: 'var(--tx-3)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx-2)', flexShrink: 0 }}>{pct.toFixed(0)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fund Treemap */}
      <FundTreemap assets={assets} buckets={buckets} onNavigateToFund={onNavigateToFund} />
    </div>
  );
}

// ── Price Update Progress Bar ──────────────────────────────────────────────────
function PriceUpdateBar({ progress }: { progress: UpdateProgress | null }) {
  if (!progress) return null;
  const { pct, message, done, errors } = progress;
  const hasErrors = errors.length > 0;

  return (
    <div style={{
      borderRadius: 14,
      padding: '14px 18px',
      background: done
        ? hasErrors ? 'rgba(251,191,36,0.07)' : 'rgba(16,185,129,0.07)'
        : 'var(--bg-card)',
      border: `1px solid ${done ? (hasErrors ? 'rgba(251,191,36,0.25)' : 'rgba(16,185,129,0.25)') : 'var(--bd)'}`,
      transition: 'all 0.3s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {done
            ? hasErrors
              ? <AlertCircle size={14} color="#fbbf24" />
              : <CheckCircle size={14} color="#10b981" />
            : <RefreshCw size={14} color="#38bdf8" style={{ animation: 'spin 1s linear infinite' }} />}
          <span style={{ fontSize: 13, fontWeight: 600, color: done ? (hasErrors ? '#fbbf24' : '#10b981') : 'var(--tx-2)' }}>
            {message}
          </span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: done ? '#10b981' : '#38bdf8', minWidth: 38, textAlign: 'right' }}>
          {pct}%
        </span>
      </div>

      {/* Track */}
      <div style={{ height: 5, borderRadius: 3, background: 'var(--bd)', overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          borderRadius: 3,
          width: `${pct}%`,
          background: done
            ? hasErrors ? 'linear-gradient(90deg,#fbbf24,#d97706)' : 'linear-gradient(90deg,#10b981,#059669)'
            : 'linear-gradient(90deg,#38bdf8,#6366f1)',
          transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>

      {/* Error list */}
      {done && hasErrors && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#fbbf24' }}>
          {errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
const LAST_UPDATE_KEY = 'wealthos:lastPriceUpdate';

export function Dashboard({ onNavigateToFund }: { onNavigateToFund?: (fundId: string) => void } = {}) {
  const { state, dispatch } = useAppState();
  const { assets, transactions, buckets, liabilities, assetClasses, priceHistory, moneyTransactions = [], holdings } = state;

  // Price engine state
  const [progress,    setProgress]    = useState<UpdateProgress | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(() => localStorage.getItem(LAST_UPDATE_KEY));

  const handlePriceUpdate = useCallback(async () => {
    if (progress && !progress.done) return; // already running
    setProgress({ pct: 0, phase: 1, message: 'Khởi động Price Engine...', done: false, results: [], errors: [] });
    try {
      const currentHoldings = holdings || [];
      const results = await fetchAllPrices(assets, setProgress, currentHoldings);
      if (results.length > 0) {
        // Map results → BULK_UPDATE_PRICES payload (code → code matching)
        dispatch({
          type: 'BULK_UPDATE_PRICES',
          payload: results.map(r => ({ code: r.code, price: r.price })),
        });
        // Rollup all composite assets whose holdings were updated
        if (currentHoldings.length > 0) {
          const compositeIds = Array.from(new Set(currentHoldings.map(h => h.assetId)));
          compositeIds.forEach(assetId => {
            dispatch({ type: 'ROLLUP_COMPOSITE_ASSET', payload: { assetId } });
          });
        }
        const now = new Date().toISOString();
        localStorage.setItem(LAST_UPDATE_KEY, now);
        setLastUpdated(now);
      }
    } catch (e) {
      setProgress(p => p ? { ...p, done: true, errors: [...(p.errors), 'Lỗi không xác định'] } : p);
    }
    // Auto-hide after 5 s
    setTimeout(() => setProgress(null), 5_000);
  }, [assets, holdings, dispatch, progress]);

  // Ví chung — lấy trực tiếp từ tài sản đặc biệt (không tính lại từ formula)
  const walletAsset  = assets.find(a => a.id === CASH_WALLET_ASSET_ID);
  const globalWallet = walletAsset?.currentPrice ?? 0;

  const totalNAV     = getTotalNAV(assets); // wallet đã là 1 asset trong danh sách
  const totalCost    = getTotalCost(assets);
  const totalDebt    = getTotalLiabilities(liabilities || []);
  const netWorth     = totalNAV - totalDebt;
  const unrealized   = totalNAV - totalCost;
  const unrealizedPct = totalCost > 0 ? (unrealized / totalCost) * 100 : 0;
  const investAssets0 = assets.filter(a => a.id !== CASH_WALLET_ASSET_ID);
  // Kế hoạch thu nhập — per-asset: dùng estimatedIncome nếu đã thiết lập, else yieldBased
  // (tránh global max làm inflate khi yieldBased > estimatedIncome cho toàn portfolio)
  const annualIncome = investAssets0.reduce((s, a) => {
    const est = getAssetAnnualIncome(a);
    const yld = getAssetCurrentValue(a) * ((a as any).yieldRate ?? 0) / 100;
    return s + (est > 0 ? est : yld);
  }, 0);

  // ── Performance metrics ──────────────────────────────────────────────────
  const leverageMultiple = netWorth > 0 ? totalNAV / netWorth : 1;
  const leverageLevel    = leverageMultiple < 1.2 ? 'An toàn' : leverageMultiple < 2 ? 'Vừa phải' : leverageMultiple < 4 ? 'Cao' : 'Rất cao';
  const leverageColor    = leverageMultiple < 1.2 ? '#10b981' : leverageMultiple < 2 ? '#fbbf24' : '#f87171';

  // Tổng nợ
  const debtRatio    = totalNAV > 0 ? (totalDebt / totalNAV) * 100 : 0;
  const debtLevel    = debtRatio < 20 ? 'An toàn' : debtRatio < 40 ? 'Bình thường' : 'Cảnh báo';
  const debtLevelColor = debtRatio < 20 ? '#10b981' : debtRatio < 40 ? '#fbbf24' : '#f87171';

  // Thu nhập thực nhận — tổng giao dịch INCOME trong năm hiện tại
  const currentYear    = new Date().getFullYear();
  const realizedIncome = (moneyTransactions as any[])
    .filter((t: any) => t.type === 'INCOME' && new Date(t.date).getFullYear() === currentYear)
    .reduce((s: number, t: any) => s + (t.net_amount ?? 0), 0);

  // Lãi/Lỗ — dùng tổng vốn đầu tư từ giao dịch thực tế (DEPOSIT + BUY external - WITHDRAW)
  // so với tổng NAV hiện tại (gồm ví). Cách này phản ánh tổng lợi nhuận (tăng giá + thu nhập tích luỹ).
  const allTxs = moneyTransactions as any[];
  const netCapitalInvested = allTxs.reduce((s: number, t: any) => {
    if (t.type === 'DEPOSIT') return s + (t.net_amount ?? 0);
    if (t.type === 'WITHDRAW') return s - (t.net_amount ?? 0);
    if (t.type === 'BUY' && t.from_node?.kind === 'external') return s + (t.net_amount ?? 0);
    if (t.type === 'SELL' && t.to_node?.kind === 'external') return s - (t.net_amount ?? 0);
    return s;
  }, 0);
  // Fallback: nếu chưa tạo Giao dịch gốc, dùng costPrice field
  const assetCostFallback = investAssets0.reduce((s, a) => s + a.costPrice * a.quantity, 0);
  const totalCostBasis    = netCapitalInvested > 0 ? netCapitalInvested : assetCostFallback;
  const capitalPnl        = totalNAV - totalCostBasis;
  const capitalPnlPct     = totalCostBasis > 0 ? (capitalPnl / totalCostBasis) * 100 : 0;
  const hasCapitalPnl     = totalCostBasis > 0;

  // ── NAV trend ────────────────────────────────────────────────────────────
  const navPoints = [0.81, 0.85, 0.88, 0.92, 0.96, 1].map(r => totalNAV * r);
  const navTrend  = [
    { m: 'T10', v: navPoints[0] }, { m: 'T11', v: navPoints[1] },
    { m: 'T12', v: navPoints[2] }, { m: 'T1',  v: navPoints[3] },
    { m: 'T2',  v: navPoints[4] }, { m: 'T3',  v: navPoints[5] },
  ];

  // ── Fund Goal bar data ────────────────────────────────────────────────────
  const fundGoalData = buckets
    .filter(b => b.targetValue > 0)
    .map(b => {
      const { nav } = getBucketProgress(b, assets);
      const pct  = Math.min((nav / b.targetValue) * 100, 150);
      const met  = nav >= b.targetValue;
      return {
        name:      b.name,
        nav:       Math.round(nav       / 1e9 * 10) / 10,
        target:    Math.round(b.targetValue / 1e9 * 10) / 10,
        navFull:   nav,
        tgtFull:   b.targetValue,
        pct, met,
        color:     b.color,
      };
    });

  const topAssets = [...assets]
    .sort((a, b) => getAssetUnrealizedGainPercent(b) - getAssetUnrealizedGainPercent(a))
    .slice(0, 6);

  const lastUpdatedLabel = lastUpdated
    ? `Cập nhật lúc ${new Date(lastUpdated).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}`
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Price Update Bar ── */}
      <PriceUpdateBar progress={progress} />

      {/* ══════════════════════════════════════════════════════════════
          3-COLUMN HEADER
      ══════════════════════════════════════════════════════════════ */}
      <div className="dashboard-grid">

        {/* ── Cột 1: OVERVIEW ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Header row: label + update button */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <SectionLabel icon={<Wallet size={13} color="#34d399" />} label="Overview" color="#34d399" />
            <button
              onClick={handlePriceUpdate}
              disabled={!!(progress && !progress.done)}
              title="Cập nhật giá thị trường"
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 8, border: '1px solid var(--bd)',
                background: 'var(--bg-card-el)', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, color: 'var(--tx-3)',
                transition: 'all 0.15s',
                opacity: progress && !progress.done ? 0.5 : 1,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#38bdf8'; (e.currentTarget as HTMLButtonElement).style.color = '#38bdf8'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--bd)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-3)'; }}
            >
              <RefreshCw size={11} style={progress && !progress.done ? { animation: 'spin 1s linear infinite' } : {}} />
              Cập nhật thị trường
            </button>
          </div>

          {/* Tổng tài sản */}
          <KpiCard
            label="Tổng tài sản"
            value={<SensitiveValue>{formatVND(totalNAV)}</SensitiveValue>}
            valueColor="#34d399"
            bg="var(--kpi-green)"
            border="1px solid rgba(52,211,153,0.2)"
            accent="rgba(52,211,153,0.05)"
            insight={
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>
                    Tài sản đầu tư <strong style={{ color: 'var(--tx-3)' }}><SensitiveValue>{formatVND(getTotalNAV(assets))}</SensitiveValue></strong>
                  </span>
                  {lastUpdatedLabel && (
                    <span style={{ fontSize: 10, color: 'var(--tx-5)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                      {lastUpdatedLabel}
                    </span>
                  )}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10 }}>🪙</span>
                  Ví chung <strong style={{ color: globalWallet >= 0 ? '#a78bfa' : '#f87171', marginLeft: 4 }}><SensitiveValue>{formatVND(globalWallet)}</SensitiveValue></strong>
                </span>
              </span>
            }
          />

          {/* Tổng nợ */}
          <KpiCard
            label="Tổng nợ"
            value={totalDebt > 0 ? <SensitiveValue>{formatVND(totalDebt)}</SensitiveValue> : '—'}
            valueColor={totalDebt > 0 ? '#f87171' : 'var(--tx-4)'}
            bg="var(--kpi-red)"
            border="1px solid rgba(239,68,68,0.16)"
            insight={
              totalDebt > 0 ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <TrendingDown size={10} color={debtLevelColor} />
                  Nợ/TS <strong style={{ color: debtLevelColor }}>{debtRatio.toFixed(1)}%</strong>
                  <span style={{ padding: '1px 5px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${debtLevelColor}18`, color: debtLevelColor }}>{debtLevel}</span>
                </span>
              ) : <span>Không có khoản vay</span>
            }
          />

          {/* Tài sản ròng */}
          <KpiCard
            label="Tài sản ròng (Net Worth)"
            value={<SensitiveValue>{formatVND(netWorth)}</SensitiveValue>}
            valueColor={netWorth >= 0 ? '#38bdf8' : '#f87171'}
            bg="var(--kpi-sky-deep)"
            border="1px solid rgba(56,189,248,0.18)"
            insight={
              <span>
                Vốn sạch <strong style={{ color: '#38bdf8' }}>
                  {totalNAV > 0 ? ((netWorth / totalNAV) * 100).toFixed(1) : '100.0'}%
                </strong>
                {' '}tổng tài sản
              </span>
            }
          />
        </div>

        {/* ── Cột 2: PHÂN BỔ ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SectionLabel icon={<PieIcon size={13} color="#a78bfa" />} label="Allocation" color="#a78bfa" />
          <ColPhanBo assets={assets} assetClasses={assetClasses || []} buckets={buckets} onNavigateToFund={onNavigateToFund} />
        </div>

        {/* ── Cột 3: PERFORMANCE ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SectionLabel icon={<TrendingUp size={13} color="#fbbf24" />} label="Performance" color="#fbbf24" />

          {/* Đòn bẩy nợ */}
          <KpiCard
            label="Đòn bẩy nợ"
            value={`${leverageMultiple.toFixed(2)}x`}
            valueColor="var(--tx-1)"
            bg="var(--bg-card)"
            border="1px solid var(--bd)"
            insight={
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Mức độ:{' '}
                <span style={{ padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600, background: `${leverageColor}18`, color: leverageColor }}>{leverageLevel}</span>
                <span style={{ color: 'var(--tx-5)' }}>· Tổng TS / Vốn chủ</span>
              </span>
            }
          />

          {/* Thu nhập thực nhận */}
          <KpiCard
            label={`Thu nhập thực nhận (${currentYear})`}
            value={realizedIncome > 0 ? <SensitiveValue>{formatVND(realizedIncome)}</SensitiveValue> : '—'}
            valueColor={realizedIncome > 0 ? '#10b981' : 'var(--tx-4)'}
            bg={realizedIncome > 0 ? 'var(--kpi-green)' : 'var(--bg-card)'}
            border={realizedIncome > 0 ? '1px solid rgba(16,185,129,0.18)' : '1px solid var(--bd)'}
            insight={
              <span>
                Cổ tức + lãi đã nhận ·{' '}
                {annualIncome > 0 && (
                  <span>Kế hoạch <strong style={{ color: '#10b981' }}><SensitiveValue>{formatVND(annualIncome)}</SensitiveValue></strong>/năm</span>
                )}
              </span>
            }
          />

          {/* Lãi/Lỗ Vốn */}
          <KpiCard
            label="Lãi / Lỗ Vốn"
            value={
              !hasCapitalPnl
                ? <span style={{ fontSize: 14, color: 'var(--tx-4)' }}>Chưa có giao dịch</span>
                : <SensitiveValue>{capitalPnl >= 0 ? '+' : ''}{formatVND(capitalPnl)}</SensitiveValue>
            }
            valueColor={capitalPnl >= 0 ? '#34d399' : '#f87171'}
            bg={capitalPnl >= 0 ? 'rgba(52,211,153,0.05)' : 'rgba(239,68,68,0.05)'}
            border={`1px solid ${capitalPnl >= 0 ? 'rgba(52,211,153,0.15)' : 'rgba(239,68,68,0.12)'}`}
            insight={
              hasCapitalPnl ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {capitalPnl >= 0 ? <ArrowUpRight size={11} color="#34d399" /> : <ArrowDownRight size={11} color="#f87171" />}
                  <strong style={{ color: capitalPnl >= 0 ? '#34d399' : '#f87171' }}>{Math.abs(capitalPnlPct).toFixed(2)}%</strong>
                  <span>tổng lợi nhuận · vốn ròng <SensitiveValue>{formatVND(totalCostBasis)}</SensitiveValue></span>
                </span>
              ) : (
                <span>Nhập giao dịch BUY để tính lãi/lỗ vốn</span>
              )
            }
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          FUND GOALS — Horizontal Bar Chart
      ══════════════════════════════════════════════════════════════ */}
      {fundGoalData.length > 0 && (
        <Card>
          <div style={{ padding: '14px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>Tiến độ Mục tiêu Quỹ</div>
              <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>NAV hiện tại vs. mục tiêu chiến lược</div>
            </div>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {fundGoalData.map(f => {
              const pct    = f.navFull > 0 && f.tgtFull > 0 ? Math.min((f.navFull / f.tgtFull) * 100, 110) : 0;
              const barPct = Math.min(pct, 100);
              const over   = f.navFull >= f.tgtFull;
              return (
                <div key={f.name} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 3, backgroundColor: f.color }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{f.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: over ? '#10b981' : 'var(--tx-2)' }}><Money value={f.navFull} /></span>
                      <span style={{ fontSize: 11, color: 'var(--tx-5)' }}>/ <Money value={f.tgtFull} /></span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 5,
                        background: over ? 'rgba(16,185,129,0.12)' : 'rgba(100,116,139,0.12)',
                        color: over ? '#10b981' : '#64748b',
                      }}>{pct.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, backgroundColor: 'var(--bd)', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 3,
                      width: `${barPct}%`,
                      background: over
                        ? 'linear-gradient(90deg, #059669, #10b981)'
                        : `linear-gradient(90deg, ${f.color}88, ${f.color})`,
                      transition: 'width 0.7s ease',
                    }} />
                  </div>
                  {!over && (
                    <div style={{ fontSize: 10, color: 'var(--tx-5)', marginTop: 3 }}>
                      Còn thiếu <strong style={{ color: 'var(--tx-4)' }}><Money value={f.tgtFull - f.navFull} /></strong>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════════
          NAV TREND 6 THÁNG  (preserved)
      ══════════════════════════════════════════════════════════════ */}
      <Card>
        <div style={{ padding: '16px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--tx-4)', marginBottom: 2 }}>Biến động Tổng NAV · 6 tháng</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--tx-1)', letterSpacing: '-0.02em' }}><Money value={totalNAV} /></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)' }}>
              <ArrowUpRight size={12} color="#34d399" />
              <span style={{ fontSize: 12, color: '#34d399', fontWeight: 600 }}>+{unrealizedPct.toFixed(1)}%</span>
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 4px 8px' }}>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={navTrend} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#059669" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
              <XAxis dataKey="m" tick={{ fill: 'var(--chart-tick)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip formatter={(v: any) => [<Money value={Number(v)} />, 'NAV']} {...tooltipStyle} />
              <Area type="monotone" dataKey="v" stroke="#34d399" strokeWidth={1.8}
                fill="url(#navGrad)" dot={false}
                activeDot={{ r: 4, fill: '#34d399', stroke: 'var(--bg-card)', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* ══════════════════════════════════════════════════════════════
          PHÂN BỔ THEO QUỸ  (preserved)
      ══════════════════════════════════════════════════════════════ */}
      <Card>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--bd)' }}>
          <div style={{ fontSize: 12, color: 'var(--tx-4)' }}>Phân bổ theo Quỹ</div>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 160px), 1fr))', gap: 10 }}>
            {buckets.map((b) => {
              const { nav, pct, diff } = getBucketProgress(b, assets);
              const hasTarget = b.targetValue > 0;
              const over = diff !== null && diff >= 0;
              return (
                <div key={b.id} style={{ padding: '14px 16px', borderRadius: 12, background: `linear-gradient(135deg, ${b.color}08 0%, transparent 100%)`, border: `1px solid ${!over && hasTarget ? 'rgba(251,191,36,0.18)' : `${b.color}18`}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 3, backgroundColor: b.color }} />
                    <span style={{ fontSize: 11, color: 'var(--tx-3)', fontWeight: 500 }}>{b.name}</span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx-1)', letterSpacing: '-0.02em', marginBottom: 6 }}><Money value={nav} /></div>
                  {hasTarget && pct !== null && (
                    <>
                      <div style={{ height: 3, borderRadius: 2, backgroundColor: 'var(--bd)', overflow: 'hidden', marginBottom: 6 }}>
                        <div style={{ height: '100%', borderRadius: 2, width: `${Math.min(pct, 130)}%`, backgroundColor: over ? b.color : '#fbbf24', transition: 'width 0.6s ease' }} />
                      </div>
                      <div style={{ fontSize: 10, color: over ? '#10b981' : '#fbbf24' }}>
                        {over ? <>Dư <Money value={diff!} /></> : <>Thiếu <Money value={Math.abs(diff!)} /></>}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--tx-5)', marginTop: 2 }}>{pct.toFixed(0)}% / <Money value={b.targetValue} /></div>
                    </>
                  )}
                  {!hasTarget && <div style={{ fontSize: 10, color: 'var(--tx-4)' }}>Chưa có mục tiêu</div>}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* ══════════════════════════════════════════════════════════════
          HIỆU SUẤT TÀI SẢN  (preserved)
      ══════════════════════════════════════════════════════════════ */}
      <Card>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--bd)' }}>
          <div style={{ fontSize: 12, color: 'var(--tx-4)' }}>Hiệu suất tài sản (Top 6)</div>
        </div>
        <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--bd)' }}>
                {['Tên tài sản', 'Lớp', 'Giá vốn', 'Giá trị', 'Lợi nhuận', '%'].map((h, i) => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: i > 1 ? 'right' as const : 'left' as const, fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topAssets.map((a, i) => {
                const gain = getAssetUnrealizedGain(a);
                const pct  = getAssetUnrealizedGainPercent(a);
                const pos  = gain >= 0;
                return (
                  <tr key={a.id}
                    style={{ borderBottom: i < topAssets.length - 1 ? '1px solid var(--bd-sub)' : 'none', transition: 'background 0.1s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--bg-row-hover)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'; }}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--tx-1)' }}>{a.name}</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <Badge label={a.assetClass} size="xs" color={resolveClassColor(a.assetClass, assetClasses)} />
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 12, color: 'var(--tx-3)' }}><Money value={a.costPrice * a.quantity} /></td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}><Money value={a.currentPrice * a.quantity} /></td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: pos ? '#10b981' : '#f87171' }}>
                      <Money value={gain} prefix={pos ? '+' : ''} />
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: pos ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: pos ? '#10b981' : '#f87171' }}>
                        {pos ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
                        {Math.abs(pct).toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
